import classNames from "classnames";
import * as React from "react";

import {staticUrl} from "@khan/urls";

import {
    getAvatarDisplayName,
    getAvatarStyleDescription,
} from "#profile-nav-package/strings.ts";

import type {AvatarModel, AvatarPart, AvatarPartType} from "./types.ts";

type Props = {
    className?: string;
    avatarParts?: Array<AvatarPart>;
    avatarModels?: Array<AvatarModel>;
    avatarPartType: AvatarPartType;
    onPartClick: (arg1: string) => void;
    selectedPart: string;
    showPartName: boolean;
    selectedModel?: AvatarModel;
    visible: boolean;
};
/**
 * Shows a drawer of parts or models that can be used to customize a user's
 * avatar.
 *
 * One of avatarParts or avatarModels must be set in order for this to render
 * anything.
 */
export default class AvatarDrawer extends React.Component<Props> {
    render(): React.ReactNode {
        const {avatarParts, avatarModels, selectedModel} = this.props;
        const model = selectedModel;

        const avatarComponents = avatarParts ?? avatarModels;
        if (!avatarComponents) {
            return null;
        }

        const parts = avatarComponents.map((part) => {
            let requirements = part.requirements.join(";");
            let isAvailable = part.isAvailable;

            // Style requirements overrule the base model requirements, unless
            // the style itself has no requirements, in which case we fallback
            // on the model requirements.
            // TODO(david): Extract this logic into a fn and re-use in
            //     AvatarPreview
            if (this.props.avatarPartType === "style" && model) {
                requirements = requirements || model.requirements.join(";");
                isAvailable = isAvailable && model.isAvailable;
            }

            const classes = classNames({
                "button-reset": true,
                "avatar-part": true,
                available: isAvailable,
                "not-available": !isAvailable,
                selected: part.name === this.props.selectedPart,
            });

            return (
                <button
                    className={classes}
                    key={part.name}
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={this.props.onPartClick.bind(null, part.name)}
                    // Remove from keyboard navigation if not on screen
                    tabIndex={this.props.visible ? 0 : -1}
                >
                    {requirements && !isAvailable && (
                        <div className="part-requirements">
                            <span className="part-requirements-text">
                                {requirements}
                            </span>
                        </div>
                    )}
                    {!isAvailable && (
                        <i className="icon-lock locked-part-icon" />
                    )}
                    {this.props.showPartName && (
                        <div className="avatar-part-name">
                            {getAvatarDisplayName(part.name)}
                        </div>
                    )}
                    <img
                        className="avatar-part-preview"
                        src={staticUrl(part.imageSrc)}
                        alt={getAvatarStyleDescription(part.name)}
                    />
                </button>
            );
        });

        const classes = [
            "avatar-parts-drawer fancy-scrollbar",
            this.props.className || "",
            `${this.props.avatarPartType}-parts`,
        ];

        return <div className={classes.join(" ")}>{parts}</div>;
    }
}
